import React, { useRef, useEffect, useState } from 'react';
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useReportQueueStore } from '../../store';

const PAGE_SIZE = 5;

const NICOLE_AVATAR = 'https://cdn.pawnec.com/petpulse/Vetscribe%20AI%20-%2003%20Clinic%20Data%20Analyst%20Avatar.gif';

const Spinner = () => (
  <svg className="h-5 w-5 animate-spin text-[#2492B6]" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const STATUS_TEXT = {
  building: 'Preparing report…',
  downloaded: 'Downloaded',
  failed: 'Failed to prepare',
};

function ReportJobRow({ job, onDismiss }) {
  const isActive = job.status === 'building';
  const subtitle = [job.city, job.dateRange].filter(Boolean).join(' · ');

  return (
    <li className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100 bg-gray-50/60">
      <div className="flex-shrink-0">
        {isActive && <Spinner />}
        {job.status === 'downloaded' && <CheckCircleIcon className="h-5 w-5 text-emerald-500" />}
        {job.status === 'failed' && <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />}
      </div>

      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-800 truncate">{job.label}</div>
        {subtitle && <div className="text-xs text-gray-500 truncate">{subtitle}</div>}
        <div
          className={`text-xs mt-0.5 ${
            job.status === 'failed'
              ? 'text-red-500'
              : job.status === 'downloaded'
                ? 'text-emerald-600'
                : 'text-[#2492B6]'
          }`}
        >
          {STATUS_TEXT[job.status] || job.status}
        </div>
      </div>

      {!isActive && (
        <button
          onClick={() => onDismiss(job.id)}
          className="flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label="Dismiss"
          title="Dismiss"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
    </li>
  );
}

function ReportQueueModal({ isOpen, onClose }) {
  const modalRef = useRef(null);
  const jobs = useReportQueueStore((s) => s.jobs);
  const removeJob = useReportQueueStore((s) => s.removeJob);
  const [page, setPage] = useState(0);

  const totalPages = Math.max(1, Math.ceil(jobs.length / PAGE_SIZE));

  // Keep the page in range as jobs are added/removed (and reset when reopened).
  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  useEffect(() => {
    if (isOpen) setPage(0);
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const pageJobs = jobs.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl p-6"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 mb-1">
          <img src={NICOLE_AVATAR} alt="Nicole AI" className="h-8 w-8 rounded-full object-cover" />
          <h2 className="text-xl font-bold text-[#2492B6]">Report Queue</h2>
        </div>
        <p className="text-sm text-gray-500 mb-5">
          Reports Nicole is preparing for you to download. Dismiss an entry once you're done with it.
        </p>

        {jobs.length === 0 ? (
          <div className="py-10 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <CheckCircleIcon className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">No reports in the queue.</p>
            <p className="text-xs text-gray-400 mt-1">
              Generate a report and it will appear here while it is being built.
            </p>
          </div>
        ) : (
          <>
            <ul className="space-y-2">
              {pageJobs.map((job) => (
                <ReportJobRow key={job.id} job={job} onDismiss={removeJob} />
              ))}
            </ul>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  Prev
                </button>
                <span className="text-xs text-gray-500">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ReportQueueModal;
